import { z } from "zod";
import { SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";

/**
 * Split Deposit File Step
 *
 * Follows Split Document (V2) in an "AR Deposit File Intake" run, when the
 * file dropped into a check deposit holds more than one check. Each document
 * the file was split into becomes its own row on the deposit, read by its own
 * run of the workflow, and the row for the whole file is removed. A file that
 * could not be split into at least two documents is recorded as failed.
 *
 * Nothing is written once the deposit has been submitted.
 */
export const splitDepositFileStep = defineStepConfig({
  type: "split_deposit_file",
  name: "Split Deposit File",
  description:
    "Give each document a check deposit file was split into its own row on the deposit, and read each one",
  category: "Data Access",
  libraryModule: "AccountsReceivable",

  inputSchema: z.object({
    depositBatchItemId: z
      .uuid()
      .describe("The deposit's row for the whole file"),
    documents: z
      .array(
        z.object({
          document: entityOfType(SystemEntity.CORE_DOCUMENT),
        }),
      )
      .optional()
      .describe(
        "Split Document (V2)'s All Documents: the documents the file was split into",
      ),
  }),

  outputSchema: z.object({
    status: z
      .enum(["split", "failed", "skipped"])
      .describe(
        "split: each document is on the deposit and being read; failed: the file could not be split (the reason is on the deposit); skipped: the deposit was already submitted",
      ),
    depositBatchItemIds: z
      .array(z.uuid())
      .describe("The deposit's rows for the documents"),
    failureReason: z.string().nullable(),
  }),

  configSchema: z.object({}),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "2 minutes",
    retry: {
      maximumAttempts: 3,
      initialInterval: "5 seconds",
      backoffCoefficient: 2,
    },
  },
});
